/**
 * Booking Context
 * Provides booking data and operations to child components
 * Replaces Redux state access pattern with React Context + TanStack Query
 */

import { createContext, useContext, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  useBookingQuery,
  useAcceptBooking,
  useDeclineBooking,
  useAcceptBookingWithVerification,
} from '../hooks/useBooking';
import type { Booking } from '../types';

/**
 * Booking Context value type
 */
interface BookingContextValue {
  // Booking data
  uuid: string | undefined;
  booking: Booking | Record<string, never>;
  loading: boolean;
  loadError: boolean;
  error: Error | null;

  // Refresh function
  refetchBooking: () => void;

  // Accept booking (on-account)
  acceptBooking: (bookingId: number) => void;
  processingAccept: boolean;
  acceptSuccess: boolean;
  acceptError: boolean;
  resetAccept: () => void;

  // Decline booking
  declineBooking: (bookingId: number) => void;
  processingDecline: boolean;
  declineSuccess: boolean;
  declineError: boolean;
  resetDecline: () => void;

  // Accept with payment verification
  acceptWithVerification: (bookingId: number) => void;
  processingVerification: boolean;
  verificationSuccess: boolean;
  verificationError: boolean;
  resetVerification: () => void;
}

// Create the context
const BookingContext = createContext<BookingContextValue | null>(null);

interface BookingProviderProps {
  children: ReactNode;
}

/**
 * BookingProvider component
 * Wraps components that need access to booking data
 */
export function BookingProvider({ children }: BookingProviderProps): JSX.Element {
  const { uuid } = useParams<{ uuid: string }>();

  // Fetch booking data
  const {
    data: booking,
    isLoading: loading,
    isError: loadError,
    error,
    refetch,
  } = useBookingQuery(uuid);

  // Mutations
  const acceptMutation = useAcceptBooking();
  const declineMutation = useDeclineBooking();
  const acceptWithVerificationMutation = useAcceptBookingWithVerification();

  // Context value
  const value: BookingContextValue = {
    // Booking data
    uuid,
    booking: booking || {},
    loading,
    loadError,
    error,

    // Refresh function
    refetchBooking: refetch,

    // Accept booking (on-account)
    acceptBooking: (bookingId: number) => acceptMutation.mutate({ bookingId, uuid }),
    processingAccept: acceptMutation.isPending,
    acceptSuccess: acceptMutation.isSuccess,
    acceptError: acceptMutation.isError,
    resetAccept: acceptMutation.reset,

    // Decline booking
    declineBooking: (bookingId: number) => declineMutation.mutate({ bookingId, uuid }),
    processingDecline: declineMutation.isPending,
    declineSuccess: declineMutation.isSuccess,
    declineError: declineMutation.isError,
    resetDecline: declineMutation.reset,

    // Accept with payment verification
    acceptWithVerification: (bookingId: number) =>
      acceptWithVerificationMutation.mutate({ bookingId, uuid }),
    processingVerification: acceptWithVerificationMutation.isPending,
    verificationSuccess: acceptWithVerificationMutation.isSuccess,
    verificationError: acceptWithVerificationMutation.isError,
    resetVerification: acceptWithVerificationMutation.reset,
  };

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
}

/**
 * Hook to access booking context
 */
export function useBookingContext(): BookingContextValue {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBookingContext must be used within a BookingProvider');
  }
  return context;
}

// Convenience hooks for specific data
interface BookingDataResult {
  booking: Booking | Record<string, never>;
  loading: boolean;
  loadError: boolean;
}

export function useBookingData(): BookingDataResult {
  const { booking, loading, loadError } = useBookingContext();
  return { booking, loading, loadError };
}

interface BookingActionsResult {
  acceptBooking: (bookingId: number) => void;
  declineBooking: (bookingId: number) => void;
  acceptWithVerification: (bookingId: number) => void;
  refetchBooking: () => void;
}

export function useBookingActions(): BookingActionsResult {
  const {
    acceptBooking,
    declineBooking,
    acceptWithVerification,
    refetchBooking,
  } = useBookingContext();
  return { acceptBooking, declineBooking, acceptWithVerification, refetchBooking };
}

interface AcceptStatusResult {
  processingAccept: boolean;
  acceptSuccess: boolean;
  acceptError: boolean;
  resetAccept: () => void;
}

export function useAcceptStatus(): AcceptStatusResult {
  const { processingAccept, acceptSuccess, acceptError, resetAccept } = useBookingContext();
  return { processingAccept, acceptSuccess, acceptError, resetAccept };
}

interface DeclineStatusResult {
  processingDecline: boolean;
  declineSuccess: boolean;
  declineError: boolean;
  resetDecline: () => void;
}

export function useDeclineStatus(): DeclineStatusResult {
  const { processingDecline, declineSuccess, declineError, resetDecline } = useBookingContext();
  return { processingDecline, declineSuccess, declineError, resetDecline };
}

interface VerificationStatusResult {
  processingVerification: boolean;
  verificationSuccess: boolean;
  verificationError: boolean;
  resetVerification: () => void;
}

export function useVerificationStatus(): VerificationStatusResult {
  const { processingVerification, verificationSuccess, verificationError, resetVerification } = useBookingContext();
  return { processingVerification, verificationSuccess, verificationError, resetVerification };
}

export default BookingContext;
